import { useUserSession } from '@solinkcorp/authentication';
import settings from '@solinkcorp/settings';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@solinkcorp/solink-ui';
import { setFilter, type SpotCheckMode } from 'actions/filterActions';
import SelectedLocations from 'components/LocationSelector/Aspen/SelectedLocations';
import { Check, ChevronDown, Info, Sparkles } from 'lucide-react';
import React from 'react';
import useDevices from 'src/hooks/devices/useDevices';
import useSites from 'src/hooks/sites/useSites';
import useLocale from 'src/hooks/useLocale';
import { useDispatch, useSelector } from 'stores/store';
import FeaturesUtil from 'utils/FeaturesUtil';
import { getSiteFeatures } from 'utils/LocationUtil';

interface SpotCheckHeaderProps {
  view: 'templates' | 'spot-check-form';
  selectedLocationIds: string[];
  selectedCameraIds: string[];
  enterCameraSelectMode: () => void;
  resetSpotCheckPanel: (resetCategoryKeys: boolean) => void;
}

export default function SpotCheckHeader(props: SpotCheckHeaderProps) {
  const {
    view,
    selectedLocationIds,
    selectedCameraIds,
    enterCameraSelectMode,
    resetSpotCheckPanel,
  } = props;
  const locale = useLocale();
  const { sites } = useSites();
  const dispatch = useDispatch();
  const { devicesBySiteId } = useDevices();
  const user = useUserSession();

  const canCreateSpotChecks = FeaturesUtil.canCreateScheduledAndEventSpotChecks(
    user.clientSettings,
    user.isSolinkUser,
  );

  const isAtLeastOneLicensedLocation = React.useMemo(
    () =>
      sites.some((site) => !!getSiteFeatures(devicesBySiteId[site.id])?.usage?.spotChecks?.enabled),
    [sites, devicesBySiteId],
  );

  const isAllLocationsLicensed = React.useMemo(
    () =>
      sites.every((site) =>
        site.id === 'all-locations'
          ? true
          : !!getSiteFeatures(devicesBySiteId[site.id])?.usage?.spotChecks?.enabled,
      ),
    [sites, devicesBySiteId],
  );

  React.useEffect(() => {
    if (!canCreateSpotChecks || !isAtLeastOneLicensedLocation) {
      dispatch(setFilter({ spotCheckMode: 'live-preview' }));
    }
  }, [canCreateSpotChecks, dispatch, isAtLeastOneLicensedLocation]);

  const modes: { id: SpotCheckMode; label: string }[] = React.useMemo(
    () => [
      { id: 'scheduled', label: locale.scheduledAIChat.scheduledSpotCheck },
      { id: 'event', label: locale.scheduledAIChat.eventSpotCheck },
      { id: 'live-preview', label: locale.scheduledAIChat.liveSpotCheck },
    ],
    [locale],
  );

  const spotCheckMode = useSelector((state) => state.filter.spotCheckMode);
  const currentMode = modes.find((mode) => mode.id === spotCheckMode);

  const hasSelectedCameras = selectedCameraIds?.length > 0;
  const selectMode = (id: SpotCheckMode) => {
    dispatch(setFilter({ spotCheckMode: id }));
    resetSpotCheckPanel(id === 'live-preview');
  };

  const isSpotCheckForm = view === 'spot-check-form';
  const isLivePreviewMode = spotCheckMode === 'live-preview';
  const viewOnlySelectMode =
    isSpotCheckForm || !canCreateSpotChecks || !isAtLeastOneLicensedLocation;
  const shouldDisplayUpgrade = !isAllLocationsLicensed || !canCreateSpotChecks;

  return (
    <div
      className="tw flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-2 py-1"
      id="spot-check-panel"
    >
      <div className="tw flex items-center gap-2">
        <Sparkles className="size-5 text-foreground-accent" aria-hidden />
        <span className="text-foreground-primary heading-sm">
          {locale.scheduledAIChat.spotCheck}
        </span>
      </div>
      <span
        className={cn('tw inline-flex', viewOnlySelectMode && 'cursor-not-allowed')}
        title={
          isLivePreviewMode && viewOnlySelectMode
            ? locale.scheduledAIChat.schedulingSpotChecksDisabled
            : undefined
        }
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={viewOnlySelectMode}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={cn('tw gap-1 capitalize', viewOnlySelectMode && 'opacity-70')}
            >
              {currentMode?.label}
              {!isSpotCheckForm || (isSpotCheckForm && isLivePreviewMode && viewOnlySelectMode) ? (
                <ChevronDown className="size-4 shrink-0 opacity-70" />
              ) : null}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="tw min-w-[14rem]">
            {modes.map((mode) => (
              <DropdownMenuItem key={mode.id} onClick={() => selectMode(mode.id)}>
                <span className="tw flex size-4 items-center justify-center">
                  {mode.id === spotCheckMode ? <Check className="size-4" /> : null}
                </span>
                {mode.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </span>
      {hasSelectedCameras ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="tw max-w-full"
          onClick={enterCameraSelectMode}
        >
          <SelectedLocations
            locationIds={selectedLocationIds}
            locationTags={undefined}
            locationsList={sites}
            maxCharacterLimit={40}
          />
          <span className="tw mx-1">-</span>
          <span>
            {selectedCameraIds.length} {locale.camera.cameras}
          </span>
        </Button>
      ) : null}
      {shouldDisplayUpgrade ? (
        <div className="tw flex items-center gap-1">
          <a
            className="tw text-text-accent body-sm hover:underline"
            href={settings.solinkUpgradeUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {locale.cloudStorage.upgrade}
          </a>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="tw inline-flex rounded-md p-1 text-foreground-muted hover:text-foreground-secondary"
                aria-label={locale.scheduledAIChat.upgradeToMoreSpotChecksInfo}
              >
                <Info className="size-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="tw max-w-xs text-left">
              {locale.scheduledAIChat.upgradeToMoreSpotChecksInfo}
            </TooltipContent>
          </Tooltip>
        </div>
      ) : null}
    </div>
  );
}
